import React, { useEffect, useState, useMemo } from "react";
import axios from "../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";
import SettlementModal from "../components/SettlementModal";
import "../styles/globalCss.css";

/* ─── helpers ────────────────────────────────────────── */

function toPersian(isoDate) {
  const d = new DateObject({ date: new Date(isoDate), calendar: gregorian, locale: gregorian_en });
  d.convert(persian, persian_fa);
  return d;
}
function persianMonthStart(year, month) {
  const d = new DateObject({ calendar: persian, locale: persian_fa, year, month, day: 1 });
  d.convert(gregorian, gregorian_en);
  return d.toDate();
}
function daysInPersianMonth(year, month) {
  if (month <= 6) return 31;
  if (month <= 11) return 30;
  return 30;
}
function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}
// number formatting that keeps the sign and locale grouping
function fmt(n) {
  const v = Number(n || 0);
  return v.toLocaleString();
}
// average price = amount (toman) / weight (gold), safe against division by zero
function avgPrice(totalToman, totalGold) {
  const gold = Number(totalGold || 0);
  if (gold <= 0) return 0;
  return Math.round(Number(totalToman || 0) / gold);
}

const PERSIAN_MONTHS   = ["فروردین","اردیبهشت","خرداد","تیر","مرداد","شهریور","مهر","آبان","آذر","دی","بهمن","اسفند"];
const PERSIAN_WEEKDAYS = ["ش","ی","د","س","چ","پ","ج"];

/* ─── Main Component ─────────────────────────────────── */
// Drawer panel state machine: null -> "day" -> "orders"
// Everything happens in ONE page, inside a single sliding drawer.

export default function SettlementHub() {
  const navigate = useNavigate();
  const today = new DateObject({ calendar: persian, locale: persian_fa });

  /* calendar */
  const [viewYear,   setViewYear]  = useState(today.year);
  const [viewMonth,  setViewMonth] = useState(today.month);
  const [summaries,  setSummaries] = useState([]);
  const [calLoading, setCalLoading] = useState(false);
  const [calError,   setCalError]   = useState("");

  /* drawer */
  const [panel,       setPanel]      = useState(null);   // null | "day" | "orders"
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayTab,      setDayTab]     = useState("all");

  /* user orders panel (inside the same drawer) */
  const [activeUser,    setActiveUser]    = useState(null);
  const [userOrders,    setUserOrders]    = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError,   setOrdersError]   = useState("");

  /* settlement action modal (opened from the orders panel) */
  const [settlementOrder, setSettlementOrder] = useState(null);

  /* ── fetch calendar ── */
  const fetchCalendar = async (year, month) => {
    setCalLoading(true);
    setCalError("");
    try {
      const start    = persianMonthStart(year, month);
      const nextMon  = month === 12 ? 1 : month + 1;
      const nextYear = month === 12 ? year + 1 : year;
      const end      = persianMonthStart(nextYear, nextMon);
      const res = await axios.get("/Settlement", {
        params: { from: start.toISOString(), to: end.toISOString() },
      });
      setSummaries(res.data?.data?.userSummaries || []);
    } catch {
      setCalError("خطا در دریافت اطلاعات");
    } finally {
      setCalLoading(false);
    }
  };

  useEffect(() => { fetchCalendar(viewYear, viewMonth); }, [viewYear, viewMonth]);

  /* ── fetch a single user's orders for the selected day (with debts/settlement info) ── */
  const fetchUserOrders = async (userId, dayData) => {
    setOrdersLoading(true);
    setOrdersError("");
    setUserOrders([]);
    try {
      const res = await axios.get("/Order/report", {
        params: {
          userId,
          from: dayData?.isoFrom,
          to:   dayData?.isoTo,
          includeDebts: true,
        },
      });
      setUserOrders(res.data.data?.orders || []);
    } catch {
      setOrdersError("خطا در دریافت سفارشات کاربر");
    } finally {
      setOrdersLoading(false);
    }
  };

  /* ── aggregate summaries into a per-day map ── */
  const dayMap = useMemo(() => {
    const map = {};
    summaries.forEach(({ userId, dailySummaries }) => {
      dailySummaries.forEach((day) => {
        const pd  = toPersian(day.date);
        const key = `${pd.year}-${String(pd.month).padStart(2,"0")}-${String(pd.day).padStart(2,"0")}`;
        if (!map[key]) {
          const start = new Date(day.date); start.setHours(0,0,0,0);
          const end   = new Date(day.date); end.setHours(23,59,59,999);
          map[key] = {
            totalOrderCount:       0,
            totalUnsettledCount:   0,
            totalUnsettledGold:    0,
            totalUnsettledToman:   0,
            totalSettledGold:      0,
            totalSettledToman:     0,
            totalSettledCount:     0,
            // ── buy / sell aggregates for the whole day ──
            totalBuyCount:         0,
            totalBuyGold:          0,
            totalBuyToman:         0,
            totalSellCount:        0,
            totalSellGold:         0,
            totalSellToman:        0,
            users: [],
            isoFrom: start.toISOString(), isoTo: end.toISOString(),
          };
        }
        map[key].totalOrderCount     += day.orderCount     || 0;
        map[key].totalUnsettledCount += day.unsettledCount || 0;
        map[key].totalUnsettledGold  += day.unsettledGold  || 0;
        map[key].totalUnsettledToman += day.unsettledToman || 0;
        map[key].totalSettledGold    += day.settledGold    || 0;
        map[key].totalSettledToman   += day.settledToman   || 0;
        map[key].totalSettledCount   += day.settledCount   || 0;

        // API تعداد دقیق سفارش‌های خرید/فروش رو جدا برنمی‌گردونه (فقط orderCount کل رو داره).
        // به همین دلیل «تعداد» رو از روی وجود مبلغ/وزن خرید یا فروش در هر رکورد تخمین می‌زنیم:
        // هر رکورد روزانه‌ی هر کاربر که مبلغ خرید غیرصفر داره یک تراکنش خرید، و همین‌طور برای فروش.
        const hasBuy  = (day.totalBuyGold || 0)  !== 0 || (day.totalBuyToman || 0)  !== 0;
        const hasSell = (day.totalSellGold || 0) !== 0 || (day.totalSellToman || 0) !== 0;

        map[key].totalBuyCount  += hasBuy  ? 1 : 0;
        map[key].totalSellCount += hasSell ? 1 : 0;

        map[key].totalBuyGold   += day.totalBuyGold   || 0;
        map[key].totalBuyToman  += day.totalBuyToman  || 0;
        map[key].totalSellGold  += day.totalSellGold  || 0;
        map[key].totalSellToman += day.totalSellToman || 0;

        map[key].users.push({
          userId,
          orderCount:       day.orderCount       || 0,
          buyCount:          hasBuy  ? 1 : 0,
          sellCount:         hasSell ? 1 : 0,
          totalBuyGold:      day.totalBuyGold      || 0,
          totalBuyToman:     day.totalBuyToman     || 0,
          totalSellGold:     day.totalSellGold     || 0,
          totalSellToman:    day.totalSellToman    || 0,
          netGold:           day.netGold           || 0,
          netToman:          day.netToman          || 0,
          unsettledGold:     day.unsettledGold     || 0,
          unsettledToman:    day.unsettledToman    || 0,
          unsettledCount:    day.unsettledCount    || 0,
          settledGold:       day.settledGold       || 0,
          settledToman:      day.settledToman      || 0,
          settledCount:      day.settledCount      || 0,
        });
      });
    });
    return map;
  }, [summaries]);

  /* ── calendar grid ── */
  const calendarGrid = useMemo(() => {
    const days     = daysInPersianMonth(viewYear, viewMonth);
    const firstDay = new DateObject({ calendar: persian, locale: persian_fa, year: viewYear, month: viewMonth, day: 1 });
    const offset   = (firstDay.weekDay.index + 1) % 7;
    const cells    = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(d);
    return cells;
  }, [viewYear, viewMonth]);

  /* ── month navigation ── */
  const prevMonth = () => { if (viewMonth === 1) { setViewYear(y=>y-1); setViewMonth(12); } else setViewMonth(m=>m-1); };
  const nextMonth = () => { if (viewMonth === 12) { setViewYear(y=>y+1); setViewMonth(1); } else setViewMonth(m=>m+1); };

  /* ── drawer handlers ── */
  const handleDayClick = (day) => {
    if (!day) return;
    const key  = `${viewYear}-${String(viewMonth).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    const data = dayMap[key];
    if (!data || data.users.length === 0) return;
    setSelectedDay({ dateStr: `${viewYear}/${viewMonth}/${day}`, dayKey: key, ...data });
    setDayTab("all");
    setPanel("day");
  };

  const handleViewUserOrders = (user) => {
    setActiveUser(user);
    fetchUserOrders(user.userId, selectedDay);
    setPanel("orders");
  };

  const closeDrawer = () => { setPanel(null); setSelectedDay(null); setActiveUser(null); setUserOrders([]); };
  const backToDay   = () => { setPanel("day"); setActiveUser(null); setUserOrders([]); setOrdersError(""); };

  /* ── filtered users for the day panel tabs ── */
  const visibleUsers = useMemo(() => {
    if (!selectedDay) return [];
    if (dayTab === "unsettled") return selectedDay.users.filter(u => u.unsettledCount > 0);
    if (dayTab === "settled")   return selectedDay.users.filter(u => u.settledCount   > 0);
    return selectedDay.users;
  }, [selectedDay, dayTab]);

  /* ═══════════════════════════ RENDER ═══════════════════════════ */
  return (
    <div className="sh-page" dir="rtl">

      {/* Header */}
      <div className="sh-header">
        <button className="sh-back-btn" onClick={() => navigate("/dashboard")}>→ بازگشت به داشبورد</button>
        <div className="sh-header-text">
          <div className="sh-eyebrow">مدیریت تسویه</div>
          <h2 className="sh-title">تقویم تسویه سفارشات</h2>
          <p className="sh-subtitle">روی هر روز کلیک کنید تا جزئیات کاربران، سفارشات و امکان تسویه را در همین صفحه ببینید</p>
        </div>
      </div>

      {calError && <p className="sh-error">{calError}</p>}

      {/* Calendar */}
      <div className="sh-card">
        <div className="sh-month-nav">
          <button className="sh-nav-btn" onClick={nextMonth} aria-label="ماه بعد">›</button>
          <h3 className="sh-month-title">{PERSIAN_MONTHS[viewMonth-1]} {viewYear}</h3>
          <button className="sh-nav-btn" onClick={prevMonth} aria-label="ماه قبل">‹</button>
        </div>

        {calLoading ? (
          <div className="sh-loading-box">در حال بارگذاری...</div>
        ) : (
          <div className="sh-grid">
            {PERSIAN_WEEKDAYS.map(wd => <div key={wd} className="sh-weekday-cell">{wd}</div>)}
            {calendarGrid.map((day, idx) => {
              if (!day) return <div key={`e-${idx}`} className="sh-empty-cell"/>;
              const key  = `${viewYear}-${String(viewMonth).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
              const data = dayMap[key];
              const hasAny = !!data;
              const isToday      = today.year === viewYear && today.month === viewMonth && today.day === day;
              const isSelected   = selectedDay?.dateStr === `${viewYear}/${viewMonth}/${day}`;
              return (
                <div
                  key={day}
                  className={cx(
                    "sh-day-cell",
                    hasAny     && "has-data",
                    isToday    && "is-today",
                    isSelected && "is-selected"
                  )}
                  onClick={() => handleDayClick(day)}
                >
                  <span className="sh-day-number">{day}</span>
                  {hasAny && (
                    <div className="sh-badges">
                      <div className="sh-badge-row sh-badge-buy" title="خرید">
                        <span className="sh-badge-icon">🟢</span>
                        <span className="sh-badge-text">{data.totalBuyCount} | {fmt(data.totalBuyGold)}g</span>
                      </div>
                      <div className="sh-badge-row sh-badge-sell" title="فروش">
                        <span className="sh-badge-icon">🔴</span>
                        <span className="sh-badge-text">{data.totalSellCount} | {fmt(data.totalSellGold)}g</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="sh-legend">
        <span className="sh-legend-item"><span className="sh-dot sh-dot-settled-gold"/>خرید</span>
        <span className="sh-legend-item"><span className="sh-dot sh-dot-unsettled-gold"/>فروش</span>
        <span className="sh-legend-item"><span className="sh-dot sh-dot-today"/>امروز</span>
      </div>

      {/* ══════════ SIDE DRAWER (day detail -> user orders -> settle) ══════════ */}
      {panel !== null && (
        <div className="sh-overlay" onClick={closeDrawer}>
          <div className="sh-drawer" onClick={e => e.stopPropagation()}>

            {/* Breadcrumb / nav bar — every step has its own explicit back button */}
            <div className="sh-breadcrumb">
              {panel === "orders" ? (
                <button className="sh-breadcrumb-back" onClick={backToDay}>
                  → بازگشت به {selectedDay?.dateStr}
                </button>
              ) : (
                <button className="sh-breadcrumb-back" onClick={closeDrawer}>
                  → بازگشت به تقویم
                </button>
              )}
              {panel === "orders" && (
                <span className="sh-breadcrumb-current">سفارشات کاربر {activeUser?.userId}</span>
              )}
              <button className="sh-close-btn" onClick={closeDrawer} aria-label="بستن">✕</button>
            </div>

            {/* ════ PANEL: DAY DETAIL ════ */}
            {panel === "day" && selectedDay && (
              <div className="sh-drawer-body">
                <div className="sh-drawer-heading">
                  <div className="sh-eyebrow">جزئیات روز</div>
                  <h3 className="sh-drawer-title">{selectedDay.dateStr}</h3>
                </div>

                {/* ── buy / sell summary boxes, right below the date ── */}
                <div className="sh-side-boxes">
                  <div className="sh-side-box sh-side-box-buy">
                    <div className="sh-side-box-title">🟢 خرید</div>
                    <div className="sh-side-box-row">
                      <span className="sh-side-box-label">تعداد</span>
                      <strong className="sh-side-box-value">{fmt(selectedDay.totalBuyCount)}</strong>
                    </div>
                    <div className="sh-side-box-row">
                      <span className="sh-side-box-label">وزن (گرم)</span>
                      <strong className="sh-side-box-value">{fmt(selectedDay.totalBuyGold)}</strong>
                    </div>
                    <div className="sh-side-box-row">
                      <span className="sh-side-box-label">مبلغ (تومان)</span>
                      <strong className="sh-side-box-value">{fmt(selectedDay.totalBuyToman)}</strong>
                    </div>
                    <div className="sh-side-box-row">
                      <span className="sh-side-box-label">متوسط قیمت</span>
                      <strong className="sh-side-box-value">
                        {fmt(avgPrice(selectedDay.totalBuyToman, selectedDay.totalBuyGold))}
                      </strong>
                    </div>
                  </div>

                  <div className="sh-side-box sh-side-box-sell">
                    <div className="sh-side-box-title">🔴 فروش</div>
                    <div className="sh-side-box-row">
                      <span className="sh-side-box-label">تعداد</span>
                      <strong className="sh-side-box-value">{fmt(selectedDay.totalSellCount)}</strong>
                    </div>
                    <div className="sh-side-box-row">
                      <span className="sh-side-box-label">وزن (گرم)</span>
                      <strong className="sh-side-box-value">{fmt(selectedDay.totalSellGold)}</strong>
                    </div>
                    <div className="sh-side-box-row">
                      <span className="sh-side-box-label">مبلغ (تومان)</span>
                      <strong className="sh-side-box-value">{fmt(selectedDay.totalSellToman)}</strong>
                    </div>
                    <div className="sh-side-box-row">
                      <span className="sh-side-box-label">متوسط قیمت</span>
                      <strong className="sh-side-box-value">
                        {fmt(avgPrice(selectedDay.totalSellToman, selectedDay.totalSellGold))}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* summary chips — always shown, even when value is zero */}
                <div className="sh-chips">
                  <Chip label="📦 کل معاملات"             value={selectedDay.totalOrderCount}/>
                  <Chip label="🟠 تعداد کاربر تسویه‌نشده" value={selectedDay.totalUnsettledCount} variant="chip-unsettled-gold"/>
                  <Chip label="🔴 گرم طلای تسویه‌نشده"     value={fmt(selectedDay.totalUnsettledGold)}  variant="chip-unsettled-gold"/>
                  <Chip label="🔴 تومان تسویه‌نشده"        value={fmt(selectedDay.totalUnsettledToman)} variant="chip-unsettled-toman"/>
                  <Chip label="🟢 گرم طلای تسویه‌شده"      value={fmt(selectedDay.totalSettledGold)}    variant="chip-settled-gold"/>
                  <Chip label="💚 تومان تسویه‌شده"         value={fmt(selectedDay.totalSettledToman)}   variant="chip-settled-toman"/>
                </div>

                {/* tabs */}
                <div className="sh-tabs">
                  {[{key:"all",label:"همه"},{key:"unsettled",label:"🟠 تسویه‌نشده"},{key:"settled",label:"🟢 تسویه‌شده"}].map(t => (
                    <button
                      key={t.key}
                      className={cx("sh-tab", dayTab === t.key && "active")}
                      onClick={() => setDayTab(t.key)}
                    >{t.label}</button>
                  ))}
                </div>

                {/* users table — every column shown even when zero, nothing hidden */}
                {visibleUsers.length === 0 ? (
                  <p className="sh-empty-msg">داده‌ای برای نمایش وجود ندارد</p>
                ) : (
                  <div className="sh-table-wrap">
                    <table className="sh-table">
                      <thead>
                        <tr>
                          <th className="sh-th">کاربر</th>
                          <th className="sh-th">کل سفارش</th>
                          <th className="sh-th">طلای خریداری‌شده</th>
                          <th className="sh-th">بدهکاری بابت خرید (تومان)</th>
                          <th className="sh-th">طلای فروخته‌شده</th>
                          <th className="sh-th">طلبکاری بابت فروش (تومان)</th>
                          <th className="sh-th">مانده طلا</th>
                          <th className="sh-th">مانده تومان</th>
                          <th className="sh-th col-unsettled-gold">طلای تسویه‌نشده</th>
                          <th className="sh-th col-unsettled-toman">تومان تسویه‌نشده</th>
                          <th className="sh-th"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleUsers.map((u,i) => (
                          <tr key={u.userId} className={i % 2 === 0 ? "sh-row-even" : "sh-row-odd"}>
                            <td className="sh-td">{u.userId}</td>
                            <td className="sh-td">{u.orderCount}</td>
                            <td className="sh-td">{fmt(u.totalBuyGold)}</td>
                            <td className="sh-td">{fmt(u.totalBuyToman)}</td>
                            <td className="sh-td">{fmt(u.totalSellGold)}</td>
                            <td className="sh-td">{fmt(u.totalSellToman)}</td>
                            <td className="sh-td">{fmt(u.netGold)}</td>
                            <td className="sh-td">{fmt(u.netToman)}</td>
                            <td className="sh-td col-unsettled-gold">{fmt(u.unsettledGold)}</td>
                            <td className="sh-td col-unsettled-toman">{fmt(u.unsettledToman)}</td>
                            <td className="sh-td">
                              <button className="sh-view-orders-btn" onClick={() => handleViewUserOrders(u)}>
                                سفارشات ›
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ════ PANEL: USER ORDERS (same drawer, slides in on top of day panel) ════ */}
            {panel === "orders" && (
              <div className="sh-drawer-body">
                {/* user summary */}
                {activeUser && (
                  <div className="sh-user-bar">
                    <span>👤 کاربر <strong>{activeUser.userId}</strong></span>
                    <span className="unsettled">🟠 {activeUser.unsettledCount} تسویه‌نشده</span>
                    <span className="settled">🟢 {activeUser.settledCount} تسویه‌شده</span>
                  </div>
                )}

                {ordersLoading && <div className="sh-loading-box">در حال دریافت سفارشات...</div>}
                {ordersError   && <p className="sh-error">{ordersError}</p>}

                {!ordersLoading && !ordersError && (
                  userOrders.length === 0 ? (
                    <p className="sh-empty-msg">سفارشی برای این کاربر در این روز یافت نشد</p>
                  ) : (
                    <div className="sh-table-wrap">
                      <table className="sh-table">
                        <thead>
                          <tr>
                            <th className="sh-th">کد تراکنش</th>
                            <th className="sh-th">جمع مبلغ (تومان)</th>
                            <th className="sh-th">وزن طلا (گرم)</th>
                            <th className="sh-th">نوع</th>
                            <th className="sh-th">وضعیت معامله</th>
                            <th className="sh-th">وضعیت تسویه</th>
                            <th className="sh-th">عملیات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userOrders.map((order,i) => (
                            <tr key={order.id} className={i % 2 === 0 ? "sh-row-even" : "sh-row-odd"}>
                              <td className="sh-td sh-td-mono">{order.id}</td>
                              <td className="sh-td">{fmt(order.totalAmount)}</td>
                              <td className="sh-td">{fmt(order.goldWight)}</td>
                              <td className="sh-td">{order.side}</td>
                              <td className="sh-td">
                                <span className={cx("sh-badge", order.status === "ProviderSucced" ? "status-ok" : "status-default")}>
                                  {order.status}
                                </span>
                              </td>
                              <td className="sh-td">
                                <span className={cx("sh-badge", order.settlementStatus ? "settlement-set" : "settlement-unset")}>
                                  {order.settlementStatus ?? "—"}
                                </span>
                              </td>
                              <td className="sh-td">
                                <button
                                  className="sh-settle-btn"
                                  disabled={order.status !== "ProviderSucced"}
                                  onClick={() => setSettlementOrder(order)}
                                >تسویه</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </div>
            )}

          </div>
        </div>
      )}

      {/* Settlement action modal — opened from within the orders panel above */}
      {settlementOrder && (
        <SettlementModal
          order={settlementOrder}
          onClose={() => setSettlementOrder(null)}
          onSuccess={() => {
            setSettlementOrder(null);
            // refresh that user's orders so the new status is visible immediately
            if (activeUser && selectedDay) fetchUserOrders(activeUser.userId, selectedDay);
            // refresh calendar totals in the background
            fetchCalendar(viewYear, viewMonth);
          }}
        />
      )}
    </div>
  );
}

/* ─── small chip helper ── */
function Chip({ label, value, variant }) {
  return (
    <div className={cx("sh-chip", variant)}>
      <span className="sh-chip-label">{label}</span>
      <strong className="sh-chip-value">{value}</strong>
    </div>
  );
}